<script setup lang="ts">
import { computed } from 'vue'
import { AdsIcon } from '@accor/ads-components'
import { IconCheck } from '@accor/icons'
import { useI18n } from 'vue-i18n'
import type { MenuEntry } from '@/types/navigation'

const props = defineProps<{
  entry: MenuEntry
  isActive: boolean
  href: string
  firstLevel?: boolean
}>()

const emit = defineEmits<{
  click: [entry: MenuEntry]
}>()

const { t } = useI18n()

const label = computed(() => t(props.entry.i18nLabel))
const handleClick = () => emit('click', props.entry)
</script>

<template>
  <a
    class="side-nav__link-item"
    :href="props.href"
    :target="props.entry.url ? '_blank' : undefined"
    :rel="props.entry.url ? 'noopener noreferrer' : undefined"
    :aria-current="props.isActive ? 'page' : undefined"
    @click.prevent="handleClick"
  >
    <div class="side-nav__link-content" :class="{ 'side-nav__first-level': props.firstLevel }">
      <span class="side-nav__list-label">
        <AdsIcon
          v-if="props.entry.icon?.svg"
          :icon="props.entry.icon.svg"
          size="xs"
          aria-hidden="true"
        />
        {{ label }}
      </span>
      <AdsIcon
        v-if="props.isActive"
        :icon="IconCheck"
        size="xs"
        aria-hidden="true"
        class="side-nav__check-icon"
      />
    </div>
  </a>
</template>

<style lang="scss">
@use '@/styles/variables';
@use '@/styles/utils';

.side-nav {
  &__link-item {
    display: flex;
    align-items: center;
    padding: ads.$ads-spacing-scale-md ads.$ads-spacing-scale-lg;

    &:focus-visible {
      outline: 2px solid ads.$ads-focus;
      outline-offset: -2px;
      border-radius: ads.$ads-border-radius-container-low;
    }
  }

  &__link-content {
    display: flex;
    align-items: center;
    justify-content: space-between;
    width: 100%;
  }

  &__first-level {
    @include ads-sass.body-default-02;
    gap: ads.$ads-spacing-scale-lg;
    color: ads.$ads-on-surface-mid;
  }

  &__list-label {
    display: flex;
    align-items: center;
    gap: ads.$ads-spacing-inline-lg;
  }

  &__check-icon {
    color: ads.$ads-accent;
  }
}
</style>
